import React, { useState, useEffect, useMemo } from 'react';
import { useAppSelector } from '../../redux/hooks';
import styles from './Profile.module.scss';
import ProfileSubheader from '../common/ProfileSubheader/ProfileSubheader';
import PersonalInformation from './PersonalInformation/PersonalInformation';
import GrowthPlan from './GrowthPlan/GrowthPlan';
import { SIDE_NAV_LINKS } from '../../constants/dataConstants';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import AwardDetails from '../HATV/AwardDetails/AwardDetails';
import QMonthDetails from '../HATV/HATVQMonth/QMonthDetails/QMonthDetails';
import { useGetHatvKPIListQuery } from '../../redux/services/hatvKPIAPI';
import { useProfileParams } from '../../utils/common';
import { KPIListProvider } from '../Context/KPIListContext';
import { useGetHATVBCRInfoListQuery } from '../../redux/services/hatvQmonthUplineImpactAPI';
import { MonthOptionsContext } from '../Context/MonthYearOptionsContext';
import { useGetOptionsListQuery } from '../../redux/services/optionMasterAPI';
import NotesAndDocuments from './NotesAndDocuments/NotesAndDocuments';
import TextButton from '../common/TextButton/TextButton';
import { useLazyGetLeaderProfileExportDataQuery } from '../../redux/services/leaderProfileExportAPI';
import ToastMessage from '../common/ToastMessage/ToastMessage';
import { toastMessageType } from '../../types/types';
import { withUtag } from '../../utils/analytics';
import { BusinessPerformance } from './BusinessPerformance/BusinessPerformance';

const Profile: React.FC = () => {
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const { t } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const { affiliateCode, aboNumber, type, tabId } = useProfileParams();
  const awardCode = searchParams.get('awardcode');
  const qualifiedMonth = searchParams.get('qualifiedmonth');

  const initialLink =
    tabId || searchParams.get('tab') || localStorage.getItem('selectedLink') || 'leaderInformation';
  const [activeLink, setActiveLink] = useState(initialLink);
  const [awardClosingMonth, setAwardClosingMonth] = useState<string>('');
  const [qMonthClosingMonth, setQMonthClosingMonth] = useState<string>('');
  const handleChangeTab = (tabId: string) => {
    setActiveLink(tabId);
    localStorage.setItem('selectedLink', tabId);
    // Update URL with tabId
    const url = type
      ? `/profile/${tabId}/${affiliateCode}/${aboNumber}/${type}?awardcode=${awardCode}&qualifiedmonth=${qualifiedMonth}`
      : `/profile/${tabId}/${affiliateCode}/${aboNumber}`;
    navigate(url, { replace: false });
  };

  // Update activeLink when tabId changes in URL (handles back/forward navigation)
  useEffect(() => {
    if (tabId && tabId !== activeLink) {
      setActiveLink(tabId);
      localStorage.setItem('selectedLink', tabId);
    }
  }, [tabId, location.pathname, activeLink]);

  useEffect(() => {
    type === 'awardapproval' && handleChangeTab('hatvawards');
  }, [type]);

  const userDetails: any = useAppSelector((state: any) => state.userDetails.user);
  // Redirect if user tries to access businessperformance without permission
  useEffect(() => {
    if (activeLink === 'businessperformance' && !userDetails?.isAccessibleABOGrowthCompanion) {
      const defaultTab = 'growthplan'; // change this if this become flag based
      setActiveLink(defaultTab);
      localStorage.setItem('selectedLink', defaultTab);
      const url = type
        ? `/profile/${defaultTab}/${affiliateCode}/${aboNumber}/${type}?awardcode=${awardCode}&qualifiedmonth=${qualifiedMonth}`
        : `/profile/${defaultTab}/${affiliateCode}/${aboNumber}`;
      navigate(url, { replace: true });
    }
  }, [activeLink, userDetails?.isAccessibleABOGrowthCompanion]);

  // Redirect if user tries to access businessperformance without permission
  useEffect(() => {
    if (activeLink === 'businessperformance' && !userDetails?.isAccessibleABOGrowthCompanion) {
      const defaultTab = 'growthplan'; // change this if this become flag based
      setActiveLink(defaultTab);
      localStorage.setItem('selectedLink', defaultTab);
      const url = type
        ? `/profile/${defaultTab}/${affiliateCode}/${aboNumber}/${type}?awardcode=${awardCode}&qualifiedmonth=${qualifiedMonth}`
        : `/profile/${defaultTab}/${affiliateCode}/${aboNumber}`;
      navigate(url, { replace: true });
    }
  }, [activeLink, userDetails?.isAccessibleABOGrowthCompanion]);

  const [toastConfig, setToastConfig] = useState({
    message: '',
    type: 'success' as toastMessageType,
    show: false,
  });

  const setToastMessage = (message: string, msgType: toastMessageType) => {
    setToastConfig({
      type: msgType,
      message: message,
      show: true,
    });
  };

  const [triggerExportProfile] = useLazyGetLeaderProfileExportDataQuery();

  const activeClosingMonth = activeLink === 'hatvawards' ? awardClosingMonth : qMonthClosingMonth;
  const {
    data: kpiListData,
    isLoading: isLoadingKPIList,
    isFetching: isFetchingKPIList,
    isError: isKPIListError,
    isSuccess: isKPIListSuccess,
    refetch: refetchKPIList,
  } = useGetHatvKPIListQuery(
    {
      affiliateCode,
      aboNumber,
      closingMonth: activeClosingMonth,
    },
    {
      skip: !activeClosingMonth,
    }
  );

  const {
    isFetching: isBCRFetching,
    isLoading: isBCRLoading,
    isError: isBCRAPIError,
    data: bcrData,
  } = useGetHATVBCRInfoListQuery({
    affiliateCode,
    aboNumber,
  });

  const bcrInfo = {
    bcrData,
    isBCRLoading,
    isBCRAPIError,
    isBCRFetching,
  };

  const {
    data: periodData,
    isLoading: optionLoading,
    isError: optionError,
    refetch: refetchOptions,
  } = useGetOptionsListQuery({
    applicationCode: 'amw',
    optionMasterName: 'myqmonthapproval-monthfilter',
  });

  const optionsData: OptionItem[] = periodData?.body?.options ?? [];

  const monthYearList = useMemo(() => {
    return optionsData.map(({ code }) => code);
  }, [optionsData]);

  const [selectedYearMonth, setSelectedYearMonth] = useState<string>('');
  useEffect(() => {
    if (!selectedYearMonth && monthYearList.length > 0) {
      setSelectedYearMonth(monthYearList[0]);
    }
  }, [monthYearList, selectedYearMonth]);

  const [selectedPYYearMonthValue, setSelectedPYYearMonthValue] = useState<string>('');

  const contextValue = {
    optionsData,
    monthYearList,
    selectedYearMonth,
    setSelectedYearMonth,
    selectedPYYear: selectedPYYearMonthValue,
    selectedPYYearMonth: setSelectedPYYearMonthValue,
    isLoading: optionLoading,
    isError: optionError,
    refetchOptions,
  };

  const handleProfileExport = async () => {
    setToastMessage(t('export_profile_started_toast'), 'success');

    const result = await triggerExportProfile({
      affiliateCode,
      aboNumber,
    });

    if (result.data?.body?.s3_url) {
      const link = document.createElement('a');
      link.href = result.data?.body?.s3_url;
      link.download = '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      setToastMessage(t('export_profile_failed_toast'), 'error');
    }
  };

  useEffect(() => {
    const viewUtagObject = {
      event_name: 'profile_view',
      am_country: localStorage?.getItem('userCountryCode') || '',
      am_affiliate: localStorage?.getItem('userAffiliateCode') || '',
      user_role: JSON.parse(localStorage.getItem('userRoleType') || '[]'),
      theme_toggle: currentTheme,
    };

    withUtag().then((utag) => utag.view(viewUtagObject));
  }, []);

  return (
    <MonthOptionsContext.Provider value={contextValue}>
      <KPIListProvider
        value={{
          kpiListData: kpiListData || {},
          isLoadingKPIList,
          isFetchingKPIList,
          isKPIListError,
          isKPIListSuccess,
          refetchKPIList,
        }}
      >
        <section
          className={`profile_container ${currentTheme} ${styles.profile_container}`}
          data-testid="profile_testID"
        >
          <ProfileSubheader onAwardCodeChange={() => {}} />

          <div className={styles.profile_content}>
            <div className={`flex_wrapper ${styles.flex_wrapper}`}>
              <div className={`tab_wrapper ${styles.tab_wrapper}`}>
                {SIDE_NAV_LINKS?.map((item, index) =>
                  !userDetails?.isAccessibleABOGrowthCompanion &&
                  item?.mainTabID === 'businessperformance' ? null : (
                    <div
                      key={index}
                      className={`tab_name ${styles.tab_name} ${item?.mainTabID === activeLink ? `selected ${styles.selected}` : ''}`}
                      onClick={() => handleChangeTab(item?.mainTabID)}
                    >
                      {t(`${item?.mainTabID}`)}
                    </div>
                  )
                )}
              </div>

              {activeLink === 'leaderInformation' && (
                <TextButton
                  className={`${styles.export_profile_button}`}
                  label={t('export_profile')}
                  onClick={handleProfileExport}
                  data-testid="export_profile_testID"
                  isIcon={true}
                  iconName="export_icon"
                  iconSize={16}
                  themeColor={currentTheme === 'light' ? '#2C2C2C' : '#D8D8D9'}
                />
              )}

              <div className={styles.content_wrapper} data-testid="content_wrapper_testID">
                <div className={`content_inner ${styles.inner_wrapper}`}>
                  <div
                    className={
                      activeLink === 'leaderInformation'
                        ? styles.active_section
                        : styles.hidden_section
                    }
                  >
                    <PersonalInformation onChangeTab={handleChangeTab} />
                  </div>
                  <div
                    className={
                      activeLink === 'growthplan' ? styles.active_section : styles.hidden_section
                    }
                  >
                    <GrowthPlan />
                  </div>
                  <div
                    className={
                      activeLink === 'notesdocument' ? styles.active_section : styles.hidden_section
                    }
                  >
                    <NotesAndDocuments />
                  </div>
                  {userDetails?.isAccessibleABOGrowthCompanion && (
                    <div
                      className={
                        activeLink === 'businessperformance'
                          ? styles.active_section
                          : styles.hidden_section
                      }
                    >
                      <BusinessPerformance />
                    </div>
                  )}
                  <div
                    className={
                      activeLink === 'hatvqmonth' ? styles.active_section : styles.hidden_section
                    }
                  >
                    <QMonthDetails
                      onMonthYearChange={(month: string) => setQMonthClosingMonth(month)}
                      bcrInfo={bcrInfo}
                    />
                  </div>
                  <div
                    className={
                      activeLink === 'hatvawards' ? styles.active_section : styles.hidden_section
                    }
                  >
                    <AwardDetails
                      onMonthYearChange={(month: string) => setAwardClosingMonth(month)}
                      bcrInfo={bcrInfo}
                    />
                  </div>
                </div>
              </div>
            </div>

            <ToastMessage
              message={toastConfig.message}
              toastType={toastConfig.type}
              showToast={toastConfig.show}
              setShowToast={(show) => setToastConfig((prev) => ({ ...prev, show }))}
              customStyles={{
                top: '96px',
                left: '50%',
                transform: 'translateX(-50%)',
              }}
              autoDismissal
              dismissTimeoutMs={5000}
            />
            {/* )} */}
          </div>
        </section>
      </KPIListProvider>
    </MonthOptionsContext.Provider>
  );
};

export default Profile;
